// src/components/ReservationBlock.js
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { differenceInDays, parseISO, format, startOfDay, isSameDay } from 'date-fns';
// Remove the import since we no longer need to fetch cleaners individually
// import { getCleanerForReservation } from '../supabase';
import './CalendarReservationBlock.css';

// Memoize the entire component to prevent unnecessary re-renders
const ReservationBlock = React.memo(({ 
    reservation, 
    dates, 
    status = 'confirmed', 
    onClick, 
    displayCleanerIndicator = true,
    cleanerAssignment // Add new prop for cleaner assignment
}) => {
    // We don't need to fetch or store cleaner state anymore
    // const [assignedCleaner, setAssignedCleaner] = useState(null);
    
    // Remove the effect that fetches cleaner data
    /*
    useEffect(() => {
        // Only fetch cleaner if we're displaying the indicator
        if (displayCleanerIndicator && reservation.id) {
            const fetchAssignedCleaner = async () => {
                try {
                    const assignment = await getCleanerForReservation(reservation.id);
                    if (assignment && assignment.cleaners) {
                        setAssignedCleaner(assignment.cleaners);
                    }
                } catch (error) {
                    console.error('Error fetching cleaner assignment:', error);
                }
            };
            
            fetchAssignedCleaner();
        }
    }, [reservation.id, displayCleanerIndicator]);
    */
    
    // Memoize position calculations to avoid recalculating on every render
    const { left, width, arrivalFormatted, departureFormatted } = useMemo(() => {
        // Parse the dates and normalize to start of day to avoid time-of-day issues
        const arrival = startOfDay(parseISO(reservation.arrival_date));
        const departure = startOfDay(parseISO(reservation.departure_date));
        
        // Use binary search for more efficient index finding in large date arrays
        let arrivalIndex = -1;
        let low = 0;
        let high = dates.length - 1;
        
        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            if (isSameDay(dates[mid], arrival)) {
                arrivalIndex = mid;
                break;
            } else if (dates[mid] < arrival) {
                low = mid + 1;
            } else {
                high = mid - 1;
            }
        }
        
        // If not found, calculate as fallback
        let daysFromStart = arrivalIndex !== -1 ? arrivalIndex : 0;
        
        // Duration is still calculated as difference for width
        const reservationDuration = differenceInDays(departure, arrival);

        // Calculate position and width based on days - use 60px cell width for performance
        const calculatedLeft = (daysFromStart * 60) + 30; // 60px per day cell
        const calculatedWidth = (reservationDuration * 60) - 35;

        // Format dates for display - only format what's needed
        const arrivalStr = format(arrival, 'MMM d');
        const departureStr = format(departure, 'MMM d');
        
        return {
            left: calculatedLeft,
            width: calculatedWidth,
            arrivalFormatted: arrivalStr,
            departureFormatted: departureStr
        };
    }, [reservation.arrival_date, reservation.departure_date, dates]);

    // Combined handler for showing expanded view and handling popup click - simplified
    const handleClick = useCallback((e) => {
        // If there's an onClick handler provided (for popup), call it
        if (onClick) {
            onClick(reservation);
        }
    }, [onClick, reservation]);

    // Determine class based on whether this block has a cleaner assigned
    const hasCleanerClass = displayCleanerIndicator && cleanerAssignment ? 'has-cleaner' : '';

    // Simplified render with fewer DOM nodes and conditional elements
    return (
        <div
            className={`reservation-block ${status} ${hasCleanerClass}`}
            style={{
                left: `${left}px`,
                width: `${width}px`
            }}
            onClick={handleClick}
            title={`${reservation.guest_name} - ${arrivalFormatted} to ${departureFormatted}${
                cleanerAssignment && displayCleanerIndicator ? ` - Assigned to ${cleanerAssignment.first_name} ${cleanerAssignment.last_name}` : ''
            }`}
        >
            <span className="reservation-guest">{reservation.guest_name}</span>
            
            {/* Only render cleaner indicator if needed */}
            {displayCleanerIndicator && cleanerAssignment && (
                <div 
                    className="reservation-cleaner-indicator"
                    style={{
                        backgroundColor: cleanerAssignment.color.startsWith('#') 
                            ? cleanerAssignment.color 
                            : `#${cleanerAssignment.color}`
                    }}
                >
                    {cleanerAssignment.initials}
                </div>
            )}
        </div>
    );
});

export default ReservationBlock;
